import * as React from "react"
import { Head, Link, router, usePage } from "@inertiajs/react"
import { FolderKanban, Lock, Plus } from "lucide-react"
import { Button } from "@ui/Button"
import { Card } from "@ui/Card"
import { Dialog } from "@ui/Dialog"
import { EmptyState } from "@ui/EmptyState"
import { Field } from "@ui/Field"
import { Input } from "@ui/Input"
import { Textarea } from "@ui/Textarea"
import type { Project } from "@lib/inertia-types"

export default function ProjectsIndex({ projects }: { projects: Project[] }) {
  const { url } = usePage()
  const [dialogOpen, setDialogOpen] = React.useState(url.includes("new=1"))

  return (
    <>
      <Head title="Projects · Fetch" />
      <div className="mx-auto max-w-5xl px-6 py-8">
        <div className="mb-6 flex items-center justify-between">
          <h1 className="text-title3 font-semibold text-cloverhound-gray-900 dark:text-cloverhound-gray-50">
            Projects
          </h1>
          <Button onClick={() => setDialogOpen(true)}>
            <Plus className="size-4" aria-hidden="true" />
            New project
          </Button>
        </div>

        {projects.length === 0 ? (
          <EmptyState
            icon={<FolderKanban className="size-6" aria-hidden="true" />}
            title="No projects yet"
            description="Projects group the requests, environments, credentials, and mocks for one client engagement."
            action={
              <Button onClick={() => setDialogOpen(true)}>
                <Plus className="size-4" aria-hidden="true" />
                Create your first project
              </Button>
            }
          />
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {projects.map((project) =>
              project.accessible === false ? (
                <Card key={project.id} padding="md" className="h-full opacity-60">
                  <div className="flex items-start gap-3">
                    <div
                      aria-hidden="true"
                      className="flex size-8 shrink-0 items-center justify-center rounded-md bg-cloverhound-gray-400 text-small font-bold text-white dark:bg-cloverhound-gray-700"
                    >
                      {project.name[0]?.toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <h2 className="flex items-center gap-1.5 truncate text-small font-semibold text-cloverhound-gray-700 dark:text-cloverhound-gray-300">
                        <span className="truncate">{project.name}</span>
                        <Lock className="size-3.5 shrink-0" aria-hidden="true" />
                      </h2>
                      <p className="mt-0.5 line-clamp-2 text-mini text-cloverhound-gray-600">
                        Ask an organization admin for access
                      </p>
                    </div>
                  </div>
                </Card>
              ) : (
                <Link key={project.id} href={`/projects/${project.slug}`} className="group">
                  <Card interactive padding="md" className="h-full">
                    <div className="flex items-start gap-3">
                      <div
                        aria-hidden="true"
                        className="flex size-8 shrink-0 items-center justify-center rounded-md bg-cloverhound-indigo-600 text-small font-bold text-white"
                      >
                        {project.name[0]?.toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <h2 className="truncate text-small font-semibold text-cloverhound-gray-900 group-hover:text-cloverhound-indigo-600 dark:text-cloverhound-gray-50 dark:group-hover:text-cloverhound-indigo-300">
                          {project.name}
                        </h2>
                        <p className="mt-0.5 line-clamp-2 text-mini text-cloverhound-gray-600">
                          {project.description || "No description"}
                        </p>
                      </div>
                    </div>
                  </Card>
                </Link>
              ),
            )}
          </div>
        )}
      </div>

      <NewProjectDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
    </>
  )
}

function NewProjectDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [name, setName] = React.useState("")
  const [description, setDescription] = React.useState("")
  const [submitting, setSubmitting] = React.useState(false)

  function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!name.trim()) return
    setSubmitting(true)
    router.post(
      "/projects",
      { name, description },
      { onFinish: () => setSubmitting(false) },
    )
  }

  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <Dialog.Content size="sm">
        <form onSubmit={submit}>
          <Dialog.Header>
            <Dialog.Title>New project</Dialog.Title>
            <Dialog.Description>
              One project per client engagement or integration.
            </Dialog.Description>
          </Dialog.Header>
          <Dialog.Body className="flex flex-col gap-4">
            <Field label="Name" required>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Acme Bank IVR"
                autoFocus
              />
            </Field>
            <Field label="Description">
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Webex CC integration for Acme Bank"
                rows={3}
              />
            </Field>
          </Dialog.Body>
          <Dialog.Footer>
            <Button variant="secondary" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={submitting} disabled={!name.trim()}>
              Create project
            </Button>
          </Dialog.Footer>
        </form>
      </Dialog.Content>
    </Dialog.Root>
  )
}
